import { QuoteBindingGetResponse } from '@lio/api-types';
import { QuoteBinding } from '@models/quoteBinding';
import { quoteBindingQueryKey } from '@services/quotes/quoteBinding/keys';
import { useQueries } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { differenceInMilliseconds } from 'date-fns';

export const useQuoteQuoteBindings = ({
  quoteBindings,
  enabled,
}: {
  quoteBindings: Partial<QuoteBinding>[];
  enabled: boolean;
}) => {
  const client = useClient();
  const result = useQueries({
    queries: quoteBindings?.map((qb) => ({
      queryKey: quoteBindingQueryKey.getWithParams(qb.quoteId, qb.id),
      queryFn: () => client(`quote/${qb.quoteId}/binding/${qb.id}`).json<QuoteBindingGetResponse>(),
      staleTime: 2000,
      enabled: enabled && !!qb.quoteId && !!qb.id && (qb?.status === 'Pending' || qb?.status === 'InProgress'),
      refetchInterval: (query: any) => {
        const data = query.state?.data ?? {};

        if (data?.status === 'Pending' || data?.status === 'InProgress') {
          const queryStartedAt = new Date(data.createdAt);
          const now = new Date();
          const msSinceStarted = differenceInMilliseconds(now, queryStartedAt);
          if (msSinceStarted < 10000) {
            return 3000;
          }
          if (msSinceStarted < 60000) {
            return 10000;
          }
          return 30000;
        }

        return false;
      },
    })),
    combine: (result) => {
      const data = result?.map((r) => r.data)?.filter((d) => !!d?.id);
      const isFetching = result?.some((d) => d.isFetching || d.isRefetching);

      return { data: data as QuoteBinding[], isFetching };
    },
  });

  return result;
};
